import { api } from "./apiSlice";

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface Campaign {
  id: string;
  title: string;
  type: string;
  location: string;
  organizerId: string;
  description: string;
  startTime: string;
  endTime: string;
  expectedDonors: number;
  actualDonors: number;
  contactPersonName: string;
  contactPersonPhone: string;
  medicalEstablishmentId: string;
  imageUrl: string | null;
  isActive: boolean;
  isApproved: "PENDING" | "ACCEPTED" | "CANCELLED";
  createdAt: string;
  /** Only present on getCampaignById — the backend's getCampaignById joins both. */
  establishmentName?: string;
  establishmentAddress?: string;
  organizerName?: string;
  organizerEmail?: string;
  /** Only present on getCompletedCampaignsByMedEst. */
  unitsCollected?: number;
}

interface PaginatedCampaigns {
  campaigns: Campaign[];
  pagination: { page: number; limit: number };
}

export const campaignsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCampaigns: builder.query<PaginatedCampaigns, { status?: "upcoming" | "active" | "completed"; page?: number; limit?: number } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.status) params.set("status", args.status);
        params.set("page", String(args?.page ?? 1));
        params.set("limit", String(args?.limit ?? 20));
        return `/campaigns?${params.toString()}`;
      },
      transformResponse: (res: ApiResponse<PaginatedCampaigns>) => res.data,
      providesTags: ["Campaign"],
    }),
    getUpcomingCampaigns: builder.query<PaginatedCampaigns, { limit?: number } | void>({
      query: (args) => `/campaigns/upcoming?limit=${args?.limit ?? 20}`,
      transformResponse: (res: ApiResponse<PaginatedCampaigns>) => res.data,
      providesTags: ["Campaign"],
    }),
    getCampaignsByOrganizer: builder.query<PaginatedCampaigns, { organizerId: string; page?: number; limit?: number }>({
      query: ({ organizerId, page, limit }) => `/campaigns/organizer/${organizerId}?page=${page ?? 1}&limit=${limit ?? 50}`,
      transformResponse: (res: ApiResponse<PaginatedCampaigns>) => res.data,
      providesTags: ["Campaign"],
    }),
    createCampaign: builder.mutation<
      Campaign,
      {
        title: string; type: "FIXED" | "MOBILE"; location: string; motivation: string; description: string;
        startTime: string; endTime: string; expectedDonors: number; contactPersonName: string;
        contactPersonPhone: string; medicalEstablishmentId: string; requirements?: unknown;
      }
    >({
      query: (body) => ({ url: "/campaigns", method: "POST", body }),
      transformResponse: (res: ApiResponse<{ campaign: Campaign }>) => res.data.campaign,
      invalidatesTags: ["Campaign"],
    }),
    updateCampaign: builder.mutation<Campaign, { campaignId: string; data: Partial<{ title: string; location: string; motivation: string; description: string; startTime: string; endTime: string; expectedDonors: number; contactPersonName: string; contactPersonPhone: string }> }>({
      query: ({ campaignId, data }) => ({ url: `/campaigns/${campaignId}`, method: "PUT", body: data }),
      transformResponse: (res: ApiResponse<Campaign>) => res.data,
      invalidatesTags: (_r, _e, { campaignId }) => [{ type: "Campaign", id: campaignId }, "Campaign"],
    }),
    deleteCampaign: builder.mutation<void, string>({
      query: (campaignId) => ({ url: `/campaigns/${campaignId}`, method: "DELETE" }),
      invalidatesTags: ["Campaign"],
    }),
    getCampaignPermissions: builder.query<{ canEdit: boolean; canDelete: boolean; reasons?: string[] }, string>({
      query: (campaignId) => `/campaigns/${campaignId}/permissions`,
      transformResponse: (res: ApiResponse<{ canEdit: boolean; canDelete: boolean; reasons?: string[] }>) => res.data,
    }),
    getCampaignStats: builder.query<{ campaign: unknown; participation: Record<string, number>; donations: { totalDonations: number; totalPointsEarned: number; bloodGroupDistribution: Record<string, number> } }, string>({
      query: (campaignId) => `/campaigns/${campaignId}/stats`,
      transformResponse: (res: ApiResponse<{ campaign: unknown; participation: Record<string, number>; donations: { totalDonations: number; totalPointsEarned: number; bloodGroupDistribution: Record<string, number> } }>) => res.data,
    }),
    getPendingCampaignsByMedEst: builder.query<PaginatedCampaigns, { medEstId: string; page?: number; limit?: number }>({
      query: ({ medEstId, page, limit }) => `/campaigns/pending/medical-establishment/${medEstId}?page=${page ?? 1}&limit=${limit ?? 20}`,
      transformResponse: (res: ApiResponse<PaginatedCampaigns>) => res.data,
      providesTags: ["Campaign"],
    }),
    getUpcomingCampaignsByMedEst: builder.query<PaginatedCampaigns, { medEstId: string; page?: number; limit?: number }>({
      query: ({ medEstId, page, limit }) => `/campaigns/upcoming/medical-establishment/${medEstId}?page=${page ?? 1}&limit=${limit ?? 20}`,
      transformResponse: (res: ApiResponse<PaginatedCampaigns>) => res.data,
      providesTags: ["Campaign"],
    }),
    getCompletedCampaignsByMedEst: builder.query<PaginatedCampaigns, { medEstId: string; page?: number; limit?: number }>({
      query: ({ medEstId, page, limit }) => `/campaigns/completed/medical-establishment/${medEstId}?page=${page ?? 1}&limit=${limit ?? 20}`,
      transformResponse: (res: ApiResponse<PaginatedCampaigns>) => res.data,
      providesTags: ["Campaign"],
    }),
    getMedEstCampaignSummary: builder.query<
      { pending: number; upcoming: number; completed: number; totalCampaignsHeld: number; totalCampaignDonors: number },
      string
    >({
      query: (medEstId) => `/campaigns/medical-establishment/${medEstId}/summary`,
      transformResponse: (res: ApiResponse<{ pending: number; upcoming: number; completed: number; totalCampaignsHeld: number; totalCampaignDonors: number }>) => res.data,
      providesTags: ["Campaign"],
    }),
    getCampaignById: builder.query<Campaign, string>({
      query: (campaignId) => `/campaigns/${campaignId}`,
      transformResponse: (res: ApiResponse<Campaign>) => res.data,
      providesTags: (_r, _e, id) => [{ type: "Campaign", id }],
    }),
    setCampaignApproval: builder.mutation<Campaign, { campaignId: string; status: "ACCEPTED" | "CANCELLED"; accessToken?: string }>({
      query: ({ campaignId, status }) => ({
        url: `/campaigns/${campaignId}/approval`,
        method: "PATCH",
        body: { status },
      }),
      transformResponse: (res: ApiResponse<Campaign>) => res.data,
      invalidatesTags: ["Campaign"],
    }),
  }),
});

export const {
  useGetCampaignsQuery,
  useGetUpcomingCampaignsQuery,
  useGetCampaignsByOrganizerQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useGetCampaignPermissionsQuery,
  useGetCampaignStatsQuery,
  useGetPendingCampaignsByMedEstQuery,
  useGetUpcomingCampaignsByMedEstQuery,
  useGetCompletedCampaignsByMedEstQuery,
  useGetMedEstCampaignSummaryQuery,
  useGetCampaignByIdQuery,
  useSetCampaignApprovalMutation,
} = campaignsApi;
